import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { 
  FaPlus, 
  FaTrash, 
  FaPencilAlt, 
  FaSave, 
  FaTimes, 
  FaLock, 
  FaShieldAlt, 
  FaShareAlt, 
  FaUserCheck,
  FaUnlockAlt,
  FaUserShield
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import CryptoJS from "crypto-js";

import MetaTags from '../../../components/MetaTags';
import { useAuth } from '../../../context/AuthContext';
import api, { fetchCdDirJogetProfileApi } from '../../../services/api';
import { fetchLookupDataApi } from '../../../services/lookupApi';
import { LOOKUP_MAPPING, CACHE_KEY_AGENCY_LOOKUPS } from '../../../utils/agencyProfileConstants';
import { transformToSelectOptions } from '../../../utils/agencyProfileHelpers';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';

import './ManageTeamMembers.css';

const SECRET = process.env.REACT_APP_CRYPTO_SECRET || 'SECRET';

const MODULE_LIST = [
    { key: 'Dashboard', label: 'Dashboard Overview', actions: ['View'] },
    { key: 'Team', label: 'Team Management', actions: ['View', 'Create', 'Edit'] }, 
    { key: 'Projects', label: 'Projects & Tasks', actions: ['View', 'Create', 'Edit'] }, 
    { key: 'Roles', label: 'Role Management', actions: ['View', 'Create', 'Edit'] }, 
    { key: 'Auditions', label: 'Auditions & Applications', actions: ['View', 'Action'] }, 
    { key: 'Reviews', label: 'Reviews', actions: ['View'] },
    { key: 'Meetings', label: 'Meetings', actions: ['View'] },
    { key: 'Billing', label: 'Billing & Payments', actions: ['View', 'Create', 'Edit'] }, 
    { key: 'Reports', label: 'Reports & Analytics', actions: ['View', 'Export'] }
];

const ManageTeamMembersPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [currentJogetRecord, setCurrentJogetRecord] = useState(null);
    const [viewMode, setViewMode] = useState('list'); 
    const [editIndex, setEditIndex] = useState(null);
    const [lookupOptions, setLookupOptions] = useState({ pocDesignation: [], pocContactType: [] });

    const { control, handleSubmit, reset, watch, setValue } = useForm({
        defaultValues: { name: '', email: '', mobile: '', designation: null, contactType: null, permissions: [], enableLogin: false, password: '', isReleased: false }
    });

    const watchedPermissions = watch('permissions') || [];
    const isLoginEnabled = watch('enableLogin');
    const isReleasedValue = watch('isReleased');

    const loadData = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            let finalLookups = {};
            const cached = localStorage.getItem(CACHE_KEY_AGENCY_LOOKUPS);
            if (cached) {
                try {
                    const bytes = CryptoJS.AES.decrypt(cached, SECRET);
                    finalLookups = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
                } catch (e) { finalLookups = null; }
            }

            if (!finalLookups || !finalLookups.pocDesignation) {
                const groups = LOOKUP_MAPPING;
                const promises = Object.entries(groups).map(([_, group]) => fetchLookupDataApi(group));
                const results = await Promise.all(promises);
                finalLookups = {};
                Object.keys(groups).forEach((key, i) => finalLookups[key] = transformToSelectOptions(results[i].data.data));
            }
            setLookupOptions(finalLookups);

            const response = await fetchCdDirJogetProfileApi(user.id);
            const jogetData = response?.data?.jogetData;
            if (jogetData) {
                setCurrentJogetRecord(jogetData);
                if (jogetData.teamMembersJson) {
                    const parsed = JSON.parse(jogetData.teamMembersJson);
                    const formatted = parsed.map(m => ({
                        ...m,
                        designation: finalLookups.pocDesignation?.find(d => d.value === (m.designation?.value || m.designation)) || m.designation,
                        contactType: finalLookups.pocContactType?.find(c => c.value === (m.contactType?.value || m.contactType)) || m.contactType,
                        isReleased: Boolean(m.isReleased)
                    }));
                    setTeamMembers(formatted);
                }
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [user]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleShare = async () => {
        const shareTitle = `Team Management - BookMyAudition`;
        const shareText = `Manage team roles and granular access permissions on BookMyAudition.`;
        const shareUrl = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (err) { console.log('Share canceled', err); }
        } else {
            try {
                await navigator.clipboard.writeText(shareUrl);
                Swal.fire({
                    icon: 'success',
                    title: 'Link Copied!',
                    text: 'Page link copied to clipboard.',
                    width: '340px',
                    background: '#121212',
                    color: '#ffffff',
                    confirmButtonColor: '#E10600',
                });
            } catch (err) { console.error('Failed to copy', err); }
        }
    };

    const handleOpenAdd = () => {
        setEditIndex(null);
        reset({ name: '', email: '', mobile: '', designation: null, contactType: null, permissions: ['Dashboard:View'], enableLogin: false, password: '', isReleased: false });
        setViewMode('form');
    };

    const handleOpenEdit = (index) => {
        setEditIndex(index);
        const member = teamMembers[index];
        reset({ ...member, password: '', isReleased: Boolean(member.isReleased) });
        setViewMode('form');
    };

    const handleDelete = (index) => {
        Swal.fire({
            title: 'Delete Team Member?',
            text: "This member will be released and removed from your team list.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#E10600',
            cancelButtonColor: '#2b2f3a',
            confirmButtonText: 'Yes, Release & Remove',
            background: '#121212',
            color: '#ffffff'
        }).then((result) => {
            if (result.isConfirmed) {
                const updated = teamMembers.filter((_, i) => i !== index);
                syncBackend(updated);
            }
        });
    };

    const handleCheckboxChange = (moduleKey, action) => {
        const permString = `${moduleKey}:${action}`;
        let currentPerms = [...watchedPermissions];
        if (currentPerms.includes(permString)) {
            currentPerms = currentPerms.filter(p => p !== permString);
        } else {
            currentPerms.push(permString);
        }
        setValue('permissions', currentPerms);
    };

    const handleFormSubmit = (data) => {
        const inputEmail = data.email?.trim().toLowerCase();
        const inputMobile = data.mobile?.trim();

        if (editIndex === null) {
            const duplicate = teamMembers.find(
                m => m.email?.trim().toLowerCase() === inputEmail || (inputMobile && m.mobile?.trim() === inputMobile)
            );
            if (duplicate) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicate Member',
                    text: `A team member with email "${data.email}" or mobile "${data.mobile}" is already in your team list!`,
                    background: '#121212',
                    color: '#ffffff',
                    confirmButtonColor: '#E10600'
                });
                return;
            }
        } else {
            const duplicate = teamMembers.find(
                (m, i) => i !== editIndex && (m.email?.trim().toLowerCase() === inputEmail || (inputMobile && m.mobile?.trim() === inputMobile))
            );
            if (duplicate) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicate Email/Mobile',
                    text: `Another team member already has the email "${data.email}" or mobile "${data.mobile}"!`,
                    background: '#121212',
                    color: '#ffffff',
                    confirmButtonColor: '#E10600'
                });
                return;
            }
        }

        let updatedList = [...teamMembers];
        if (editIndex !== null) updatedList[editIndex] = data;
        else updatedList.push(data);
        
        setViewMode('list');
        syncBackend(updatedList);
    };

    const syncBackend = async (listToSync) => {
        setSaving(true);
        try {
            const payloadToSync = listToSync.map(m => ({
                ...m,
                designation: m.designation?.value || m.designation,
                contactType: m.contactType?.value || m.contactType
            }));

            const payload = {
                ...currentJogetRecord,
                teamMembersJson: JSON.stringify(payloadToSync),
                user_id: user.id
            };
            await api.post('/cddir/profile/cd-dir', payload);
            
            setTeamMembers(listToSync);
            Swal.fire({ icon: 'success', title: 'Updated Successfully', timer: 1500, showConfirmButton: false, background: '#121212', color: '#fff' });
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || 'Sync Error';
            Swal.fire({ 
                icon: 'error', 
                title: 'Operation Failed',
                text: errorMsg,
                confirmButtonColor: '#E10600',
                background: '#121212',
                color: '#ffffff'
            });
            loadData();
        } finally { setSaving(false); }
    };

    if (loading) return (
      <div className="mtm-loading-box">
        <Spinner animation="border" variant="danger" />
        <p className="mt-3 text-muted">Loading Team Members...</p>
      </div>
    );

    return (
        <div className="mtm-page-container">
            <MetaTags title="Team Management" />

            {/* PAGE HEADER */}
            <div className="mtm-main-header">
                <div className="mtm-brand-header">
                    <h5>Team / Point of Contact</h5>
                    <p className="text-muted small m-0">Manage staff roles, granular module permissions, and member release status.</p>
                </div>
                <div className="mtm-header-actions">
                    <button onClick={handleShare} className="mtm-btn-share">
                        <FaShareAlt /> <span>Share Profile</span>
                    </button>
                    {viewMode === 'list' && (
                        <button className="mtm-btn-primary" onClick={handleOpenAdd}>
                            <FaPlus /> <span>Add Member</span>
                        </button>
                    )}
                </div>
            </div>

            {/* TABLE LIST VIEW */}
            {viewMode === 'list' && (
                <div className="mtm-card-box">
                    <div className="table-responsive">
                        <table className="mtm-data-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Full Name</th>
                                    <th>Email</th>
                                    <th>Designation</th>
                                    <th>Privileges</th>
                                    <th>Login</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'center' }}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamMembers.length === 0 ? (
                                    <tr>
                                      <td colSpan="8" className="text-center py-5 text-muted">
                                        <FaUserShield size={36} className="mb-2 opacity-50" />
                                        <p className="m-0">No team members added yet.</p>
                                      </td>
                                    </tr>
                                ) : (
                                    teamMembers.map((member, index) => {
                                        const modulesMap = {};
                                        (member.permissions || []).forEach(p => {
                                            const [mod, act] = p.split(':');
                                            if(!modulesMap[mod]) modulesMap[mod] = [];
                                            modulesMap[mod].push(act);
                                        });

                                        return (
                                            <tr key={index}>
                                                <td className="text-muted">{index + 1}</td>
                                                <td className="fw-bold text-white">{member.name}</td>
                                                <td className="text-muted">{member.email}</td>
                                                <td><span className="mtm-designation-badge">{member.designation?.label || member.designation}</span></td>
                                                <td>
                                                    <div className="mtm-pill-container">
                                                        {Object.entries(modulesMap).map(([mod, actions]) => (
                                                            <div key={mod} className="mtm-pill">
                                                                <span className="mtm-pill-title">{mod}</span>
                                                                <span className="mtm-pill-body">{actions.join(', ')}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>
                                                    {member.enableLogin ? (
                                                      <span className="mtm-login-enabled"><FaLock size={10} /> Enabled</span>
                                                    ) : (
                                                      <span className="mtm-login-off">Disabled</span>
                                                    )}
                                                </td>

                                                <td>
                                                    {member.isReleased ? (
                                                        <span className="mtm-status-badge badge-released"><FaUnlockAlt size={10} /> Released</span>
                                                    ) : (
                                                        <span className="mtm-status-badge badge-active"><FaUserCheck size={10} /> Active</span>
                                                    )}
                                                </td>

                                                <td>
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', width: '100%', margin: '0 auto' }}>
                                                        {/* ✅ EDIT BUTTON: ROYAL BLUE */}
                                                        <button 
                                                            type="button"
                                                            className="mtm-icon-btn mtm-edit" 
                                                            title="Edit Member" 
                                                            onClick={() => handleOpenEdit(index)}
                                                        >
                                                            <FaPencilAlt size={13} color="#ffffff" />
                                                        </button>

                                                        {/* ✅ DELETE BUTTON: CRIMSON RED */}
                                                        <button 
                                                            type="button"
                                                            className="mtm-icon-btn mtm-delete" 
                                                            title="Delete Member" 
                                                            onClick={() => handleDelete(index)}
                                                        >
                                                            <FaTrash size={13} color="#ffffff" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* FORM VIEW FOR ADD / EDIT MEMBER */}
            {viewMode === 'form' && (
                <div className="mtm-card-box mtm-form-card">
                    <div className="mtm-form-header">
                        <div className="d-flex align-items-center gap-2">
                          <div className="mtm-form-header-icon">
                            {editIndex !== null ? <FaPencilAlt /> : <FaPlus />}
                          </div>
                          <div>
                            <h5 className="m-0 text-white fw-bold">{editIndex !== null ? 'Edit Member Details' : 'Add New Team Member'}</h5>
                            <small className="text-muted">Configure staff account info, access login and permission matrix.</small>
                          </div>
                        </div>
                        {/* ✅ CLOSE BUTTON WITH HIGH VISIBILITY */}
                        <button type="button" className="mtm-close-btn" title="Close Form" onClick={() => setViewMode('list')}>
                            <FaTimes size={16} />
                        </button>
                    </div>

                    <Form onSubmit={handleSubmit(handleFormSubmit)} className="mtm-form">
                        
                        {/* CORE INFORMATION SECTION */}
                        <div className="mtm-section-label">
                          <span className="mtm-section-dot"></span> Core Information
                        </div>
                        
                        <Row className="g-3 mb-4">
                            {/* ROW 1: 4 INPUTS SIDE-BY-SIDE (3 + 3 + 3 + 3 = 12) */}
                            <Col md={3} sm={6}><FormInput name="name" label="Full Name *" control={control} isRequired /></Col>
                            <Col md={3} sm={6}><FormInput name="email" label="Email Address *" type="email" control={control} isRequired /></Col>
                            <Col md={3} sm={6}><FormInput name="mobile" label="Mobile Number *" control={control} isRequired /></Col>
                            <Col md={3} sm={6}><FormSelect name="designation" label="Designation *" control={control} options={lookupOptions.pocDesignation} isRequired /></Col>

                            {/* ROW 2: CONTACT TYPE + RELEASE TOGGLE + LOGIN ACCESS TOGGLE + PASSWORD (3 + 3 + 3 + 3 = 12) */}
                            <Col md={3} sm={6}><FormSelect name="contactType" label="Contact Type *" control={control} options={lookupOptions.pocContactType} isRequired /></Col>

                            {/* RELEASE MEMBER TOGGLE PANEL */}
                            <Col md={3} sm={6}>
                                <Form.Label className="mtm-field-label">Release Status</Form.Label>
                                <div className={`mtm-toggle-panel ${isReleasedValue ? 'panel-released' : 'panel-active'}`}>
                                    <div className="d-flex align-items-center justify-content-between h-100">
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="mtm-toggle-icon">
                                          {isReleasedValue ? <FaUnlockAlt color="#f59e0b" size={13} /> : <FaLock color="#10b981" size={13} />}
                                        </div>
                                        <div>
                                          <h6 className="m-0 text-white fw-bold" style={{ fontSize: '12px' }}>Release Member</h6>
                                          <p className="mtm-panel-desc m-0">
                                            {isReleasedValue ? "Released" : "Active"}
                                          </p>
                                        </div>
                                      </div>
                                      <Form.Check 
                                          type="switch" 
                                          id="releaseCheck" 
                                          checked={isReleasedValue} 
                                          onChange={(e) => setValue('isReleased', e.target.checked)} 
                                          className="mtm-custom-switch" 
                                      />
                                    </div>
                                </div>
                            </Col>

                            {/* USER LOGIN ACCESS TOGGLE PANEL */}
                            <Col md={3} sm={6}>
                                <Form.Label className="mtm-field-label">User Access</Form.Label>
                                <div className="mtm-toggle-panel panel-dark">
                                    <div className="d-flex align-items-center justify-content-between h-100">
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="mtm-toggle-icon">
                                          <FaUserShield color="#E10600" size={13} />
                                        </div>
                                        <div>
                                          <h6 className="m-0 text-white fw-bold" style={{ fontSize: '12px' }}>Login Access</h6>
                                          <p className="mtm-panel-desc m-0">
                                            {isLoginEnabled ? "Enabled" : "Disabled"}
                                          </p>
                                        </div>
                                      </div>
                                      <Form.Check 
                                          type="switch" 
                                          id="loginCheck" 
                                          checked={isLoginEnabled} 
                                          onChange={(e) => setValue('enableLogin', e.target.checked)} 
                                          className="mtm-custom-switch" 
                                      />
                                    </div>
                                </div>
                            </Col>

                            {/* ACCOUNT PASSWORD INPUT */}
                            {isLoginEnabled && (
                              <Col md={3} sm={6}>
                                <FormInput name="password" label="Account Password *" type="password" placeholder="Staff@123" control={control} />
                              </Col>
                            )}
                        </Row>

                        {/* MODULE ACCESS MATRIX */}
                        <div className="mtm-section-label mt-4">
                          <span className="mtm-section-dot"></span> <FaShieldAlt className="me-1"/> Module Access Matrix
                        </div>
                        
                        <div className="mtm-matrix-container mb-4">
                            <div className="mtm-matrix-header d-none d-md-flex">
                                <div className="mtm-h-col-name">MODULE NAME</div>
                                <div className="mtm-h-col-perms">ACTION PERMISSIONS</div>
                            </div>

                            {MODULE_LIST.map((mod) => (
                                <div className="mtm-matrix-row" key={mod.key}>
                                    <div className="mtm-row-label">{mod.label}</div>
                                    <div className="mtm-row-controls">
                                        {mod.actions.map(act => (
                                            <Form.Check 
                                                key={act} 
                                                type="checkbox" 
                                                label={act} 
                                                id={`chk_${mod.key}_${act}`}
                                                checked={watchedPermissions.includes(`${mod.key}:${act}`)}
                                                onChange={() => handleCheckboxChange(mod.key, act)}
                                                className="mtm-checkbox-custom"
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* BOTTOM ACTION BUTTONS */}
                        <div className="mtm-form-actions">
                            <Button type="button" className="mtm-btn-outline" onClick={() => setViewMode('list')}>
                              Discard Changes
                            </Button>
                            <Button className="mtm-btn-primary" type="submit">
                              <FaSave /> <span>Save Team Member</span>
                            </Button>
                        </div>
                    </Form>
                </div>
            )}

            {saving && <div className="mtm-floating-loader"><Spinner size="sm" className="me-2"/> Synchronizing Data...</div>}
        </div>
    );
};

export default ManageTeamMembersPage;